import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Lightbox from "@/components/Lightbox";
import Seo from "@/components/Seo";
import { GALLERY_CATEGORIES, GALLERY_ITEMS } from "@/data/gallery";
import { TRACK_GALLERY } from "@/data/track-gallery";
import { COMPANY } from "@/lib/constants";
import { TRACKS } from "@/lib/tracks";
import { PAGE_META } from "@/lib/seo-constants";
import { localBusinessSchema, breadcrumbListSchema } from "@/lib/jsonld";
import { cn } from "@/lib/utils";
import ResponsiveImg from "@/components/ResponsiveImg";

const GALLERY_COPY = {
    residential: {
        eyebrow: "Our Work",
        title: "Recent projects across the GTA.",
        sub: "Filter by service to browse a category, or click any tile to view full size.",
        note: null,
        metaKey: "gallery",
    },
    commercial: {
        eyebrow: "Commercial Work",
        title: "Commercial work across the GTA.",
        sub: "Browse by service, or click any tile to view full size.",
        note: "Representative imagery by service — real Reformity commercial project photos are being added.",
        metaKey: "commercialGallery",
    },
    industrial: {
        eyebrow: "Industrial Work",
        title: "Industrial sites & facilities.",
        sub: "Browse by service, or click any tile to view full size.",
        note: "Representative imagery by service — real Reformity industrial project photos are being added.",
        metaKey: "industrialGallery",
    },
};

export default function Gallery({ trackKey = "residential" }) {
    const track = TRACKS[trackKey];
    const copy = GALLERY_COPY[trackKey] || GALLERY_COPY.residential;
    const { categories, items } =
        trackKey === "residential"
            ? { categories: GALLERY_CATEGORIES, items: GALLERY_ITEMS }
            : TRACK_GALLERY[trackKey];
    const validCats = useMemo(() => new Set(categories.map((c) => c.id)), [categories]);

    const [searchParams, setSearchParams] = useSearchParams();
    const initialCat = searchParams.get("category");
    const [activeCat, setActiveCat] = useState(
        initialCat && validCats.has(initialCat) ? initialCat : "all",
    );
    const [lightboxIdx, setLightboxIdx] = useState(null);

    useEffect(() => {
        const fromUrl = searchParams.get("category");
        if (fromUrl && validCats.has(fromUrl) && fromUrl !== activeCat) {
            setActiveCat(fromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const filtered = useMemo(() => {
        return activeCat === "all"
            ? items
            : items.filter((g) => g.category === activeCat);
    }, [activeCat, items]);

    const handleCat = (id) => {
        setActiveCat(id);
        const next = new URLSearchParams(searchParams);
        if (id === "all") next.delete("category");
        else next.set("category", id);
        setSearchParams(next, { replace: true });
        setLightboxIdx(null);
    };

    const handlePrev = () =>
        setLightboxIdx((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length));
    const handleNext = () =>
        setLightboxIdx((i) => (i === null ? null : (i + 1) % filtered.length));

    return (
        <>
            <Seo
                {...PAGE_META[copy.metaKey]}
                path={track.gallery}
                jsonLd={[
                    localBusinessSchema(),
                    breadcrumbListSchema([
                        ...(trackKey === "residential"
                            ? []
                            : [{ name: track.label, path: track.home }]),
                        { name: "Gallery", path: track.gallery },
                    ]),
                ]}
            />
            <Section dark className="py-16 md:py-20" data-testid="gallery-hero">
                <Container>
                    <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-3">
                        {copy.eyebrow}
                    </p>
                    <h1
                        className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-none text-white"
                        data-testid="gallery-title"
                    >
                        {copy.title}
                    </h1>
                    <p className="mt-5 max-w-2xl text-base text-white/70 italic font-light">
                        {copy.sub}
                    </p>
                    {copy.note && (
                        <p className="mt-3 max-w-2xl text-xs text-white/45">
                            {copy.note}
                        </p>
                    )}
                </Container>
            </Section>

            {/* Sticky filter chips */}
            <div
                className="sticky top-16 md:top-20 z-20 bg-brand-offwhite border-y border-brand-dark/10"
                data-testid="gallery-filters"
            >
                <Container className="py-4">
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                        {categories.map((c) => {
                            const active = activeCat === c.id;
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => handleCat(c.id)}
                                    className={cn(
                                        "shrink-0 px-4 py-2 text-xs uppercase tracking-wider font-semibold border-2 transition-all duration-200",
                                        active
                                            ? "bg-brand-coral text-white border-brand-coral"
                                            : "bg-transparent text-brand-dark border-brand-dark/15 hover:border-brand-dark",
                                    )}
                                    aria-pressed={active}
                                    data-testid={`gallery-chip-${c.id}`}
                                >
                                    {c.label}
                                </button>
                            );
                        })}
                    </div>
                </Container>
            </div>

            <Section className="py-10 md:py-14" data-testid="gallery-grid-section">
                <Container size="wide">
                    {filtered.length === 0 ? (
                        <p className="text-center text-brand-dark/60 py-20">
                            No projects in this category yet.
                        </p>
                    ) : (
                        <div
                            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
                            data-testid="gallery-grid"
                        >
                            {filtered.map((item, idx) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setLightboxIdx(idx)}
                                    className="group relative block overflow-hidden bg-brand-dark aspect-[4/5] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan focus-visible:ring-offset-2"
                                    data-testid={`gallery-tile-${item.id}`}
                                >
                                    <ResponsiveImg
                                        src={item.image}
                                        alt={`${item.title} in ${item.city}`}
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                                    />
                                    <div
                                        className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/20 to-transparent opacity-90 group-hover:opacity-95 transition-opacity"
                                        aria-hidden="true"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                                        <span className="inline-block bg-brand-coral text-white text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 mb-1.5">
                                            {item.category}
                                        </span>
                                        <h3 className="font-display text-base md:text-lg tracking-wide text-white leading-tight">
                                            {item.title}
                                        </h3>
                                        <p className="text-[10px] text-white/65 mt-0.5 flex items-center gap-1">
                                            <MapPin className="h-2.5 w-2.5" strokeWidth={2.5} />
                                            {item.city}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </Container>
            </Section>

            <Section dark className="py-14 md:py-20" data-testid="gallery-cta-band">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                                Like What You See?
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-wide text-white leading-tight">
                                Let's build something just as good for you.
                            </h2>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                            <BrandButton asChild variant="primary" size="lg" className="w-full lg:w-auto">
                                <Link to={track.contact} data-testid="gallery-cta-quote">
                                    Get a Free Quote
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </BrandButton>
                            <BrandButton
                                asChild
                                variant="outline"
                                tone="onDark"
                                size="lg"
                                className="w-full lg:w-auto"
                            >
                                <a href={COMPANY.phoneHref}>
                                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                                    Call {COMPANY.phone}
                                </a>
                            </BrandButton>
                        </div>
                    </div>
                </Container>
            </Section>

            <Lightbox
                items={filtered}
                activeIndex={lightboxIdx}
                onClose={() => setLightboxIdx(null)}
                onPrev={handlePrev}
                onNext={handleNext}
            />
        </>
    );
}
